import React, { useEffect, useId, useState } from 'react';
import { FaArrowLeft, FaChevronDown, FaChevronUp, FaTimes } from 'react-icons/fa';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { resolveMediaUrl } from '../../../hooks/useStreamBootstrap.js';
import { useSongDetail } from '../../../hooks/useSongDetail.js';
import { useRequestQuote } from '../../../hooks/useRequestQuote.js';
import { isSignedIn } from '../../../_helpers/safeRedirect.js';
import { BOOST_QUICK_ADDS } from './BoostDesk.jsx';
import SongLyricLine from './SongLyricLine.jsx';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag.js';
import VinylSleeve, { coverLabel } from '../VinylSleeve.jsx';
import '../../../styles/BoostDesk.css';
import '../../../styles/SongCratePanel.css';
import '../../../styles/SongPagePanel.css';
import '../../../styles/StreamCta.css';

/**
 * The song page (mockups 22 and 26, ENG-709/717) in place of the stream
 * page's right column: the song's cover and details (`GET .../songs/{id}`,
 * ENG-710; a detail the catalogue lacks hides its tile), its first sung line,
 * and the boost desk's three-way picker for a new request — BY SPOT, BY
 * AMOUNT (+$5/+$10/+$25/+$50) and TOP SPOT — priced by the request quote
 * (ENG-710), which `queueKey` keeps current.
 *
 * Free ($0, the back of the line) is on offer only when the band takes free
 * requests (ENG-664/717). A song the show played tonight and won't repeat
 * can't be requested. `onNext(choice)` goes to the check step with
 * `{ free: true, spot }` or `{ amount, spot }`.
 */
const SongPagePanel = ({ bandId, songId, bandName = '', freeRequestsEnabled = false, queueKey, initialChoice = null, onBack, onClose, onNext, onSignIn }) => {
  const { t, format } = useLocale();
  // The lyric line is a listener lyrics surface: off unless the operator
  // flag says on (ENG-783, fail closed while loading or failed).
  const { enabled: lyricsEnabled } = useFeatureFlag('lyrics');
  const headingId = useId();
  const { status, song } = useSongDetail(bandId, songId);
  const [free, setFree] = useState(Boolean(initialChoice?.free) && freeRequestsEnabled);
  const [request, setRequest] = useState(initialChoice && !initialChoice.free ? { amount: initialChoice.amount } : {});
  const { quote, error, loading } = useRequestQuote(bandId, songId, request, queueKey);

  // Free requests switched off while the page is open: back to paid.
  useEffect(() => {
    if (!freeRequestsEnabled) setFree(false);
  }, [freeRequestsEnabled]);

  const usd = (value) => format.usd(value, { cents: 'auto' });
  const spot = free ? quote?.freeSpot ?? null : quote?.targetSpot ?? null;
  const amount = free ? 0 : quote?.amount ?? 0;
  const lastPaidSpot = quote ? quote.freeSpot - 1 : 0;
  const blocked = Boolean(song?.repeatBlocked);
  const ready = Boolean(quote) && !loading && !error && !blocked;

  const tiles = song ? [
    ['album', song.album],
    ['year', song.releaseYear ? String(song.releaseYear) : ''],
    ['genre', song.genres.join(', ')],
    ['tags', song.tags.map((tag) => tag.word).join(', ')],
    ['writtenBy', song.writtenBy],
    ['mood', song.moods.join(', ')],
  ].filter(([, value]) => value) : [];

  const next = () => {
    if (!ready) return;
    if (!isSignedIn()) {
      onSignIn?.();
      return;
    }
    onNext?.(free ? { free: true, spot } : { amount, spot });
  };

  const choosePaid = (r) => {
    setFree(false);
    setRequest(r);
  };

  const art = song ? resolveMediaUrl(song.artworkUrl) : null;

  return (
    <section className="crate-panel song-page" aria-labelledby={headingId} data-testid="song-page">
      <header className="crate-panel-header">
        <button type="button" className="song-page-back" onClick={onBack}>
          <FaArrowLeft className="rtl-mirror" aria-hidden="true" /> {t('stream.songPage.backToCrate')}
        </button>
        <button type="button" className="crate-panel-close" onClick={onClose} aria-label={t('stream.crate.panel.close')}>
          <FaTimes aria-hidden="true" />
        </button>
      </header>

      <div className="crate-panel-scroll">
        {status === 'loading' && <p className="crate-panel-status" role="status">{t('stream.crate.panel.loading')}</p>}
        {(status === 'missing' || status === 'failed') && (
          <p className="crate-panel-status" role="alert">{t(status === 'missing' ? 'stream.songPage.missing' : 'stream.songPage.failed')}</p>
        )}
        {song && (
          <>
            <div className="song-page-hero">
              <VinylSleeve
                artworkUrl={art}
                fallbackLabel={coverLabel(song.album || song.title, t('publicSongList.card.coverFallback'))}
                className="song-page-sleeve"
                coverClassName="song-page-art"
                testId="song-page-sleeve"
                active
              />
              <h2 id={headingId} className="song-page-title">{song.title}</h2>
              <p className="song-page-artist">{song.artist}</p>
            </div>

            {tiles.length > 0 && (
              <dl className="song-page-tiles">
                {tiles.map(([key, value]) => (
                  <div key={key} className="song-page-tile" data-testid={`song-page-tile-${key}`}>
                    <dt>{t(`stream.songPage.tiles.${key}`)}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            )}

            {lyricsEnabled && <SongLyricLine songId={song.id} enabled={isSignedIn()} />}

            {blocked ? (
              <p className="crate-panel-status" role="status" data-testid="song-page-blocked">{t('stream.songPage.playedTonight')}</p>
            ) : (
              <div className="boost-desk song-page-picker" role="group" aria-label={t('stream.songPage.pickerLabel', { title: song.title })}>
                <div className="boost-desk-controls">
                  <div className="boost-desk-panel">
                    <span className="boost-desk-heading">{t('stream.boost.desk.bySpot')}</span>
                    <div className="boost-desk-spot">
                      <span className="boost-desk-big" data-testid="song-page-spot">
                        {spot != null ? t('stream.boost.desk.spotValue', { spot: format.number(spot) }) : '–'}
                      </span>
                      <div className="boost-desk-stepper">
                        <button
                          type="button"
                          onClick={() => choosePaid({ target: free ? lastPaidSpot : spot - 1 })}
                          disabled={!quote || (free ? lastPaidSpot < 1 : spot <= 1)}
                          aria-label={t('stream.boost.desk.up')}
                        >
                          <FaChevronUp aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => choosePaid({ target: spot + 1 })}
                          disabled={!quote || free || spot >= lastPaidSpot}
                          aria-label={t('stream.boost.desk.down')}
                        >
                          <FaChevronDown aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="boost-desk-panel">
                    <span className="boost-desk-heading">{t('stream.boost.desk.byAmount')}</span>
                    <div className="boost-desk-amount">
                      <span className="boost-desk-big" data-testid="song-page-amount">{free ? usd(0) : usd(amount)}</span>
                      <div className="boost-desk-adds">
                        {BOOST_QUICK_ADDS.map((add) => (
                          <button
                            key={add}
                            type="button"
                            className="boost-desk-add"
                            onClick={() => choosePaid({ amount: (free ? 0 : amount) + add })}
                            aria-label={t('stream.boost.desk.addLabel', { amount: usd(add) })}
                          >
                            {t('stream.boost.desk.add', { amount: usd(add) })}
                          </button>
                        ))}
                        {freeRequestsEnabled && (
                          <button
                            type="button"
                            className={`boost-desk-add${free ? ' song-page-free--on' : ''}`}
                            aria-pressed={free}
                            onClick={() => setFree(true)}
                            data-testid="song-page-free"
                          >
                            {t('stream.songPage.free')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="boost-desk-top"
                    onClick={() => choosePaid({ target: 1 })}
                    disabled={!quote || (!free && spot === 1)}
                  >
                    <span className="boost-desk-heading">{t('stream.boost.desk.topSpot')}</span>
                    <span className="boost-desk-top-price">{quote ? usd(quote.topSpotPrice) : '–'}</span>
                    <span className="boost-desk-top-hint">{t('stream.boost.desk.tapForTop')}</span>
                  </button>
                </div>

                {freeRequestsEnabled && quote && (
                  <p className="song-page-helper" data-testid="song-page-free-helper">
                    {t('stream.songPage.freeHelper', { band: bandName, spot: format.number(quote.freeSpot) })}
                  </p>
                )}
                {error && <p className="boost-desk-status" role="alert">{t('stream.songPage.quoteFailed')}</p>}

                <button type="button" className="boost-desk-go song-page-go stream-cta" disabled={!ready} onClick={next} data-testid="song-page-request">
                  <span className="boost-desk-go-label">
                    {free
                      ? t('stream.songPage.requestFree', { title: song.title })
                      : t('stream.songPage.requestPaid', { title: song.title, amount: usd(amount) })}
                  </span>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default SongPagePanel;
